import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  fetchActiveConnection,
  fetchLedgerBids,
  fetchMainScreenActiveConnection,
  fetchSelfCapacity,
  fetchSelfCapacityById,
} from "../../../../../../Redux/Slices/CapacitySlice";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../../../../commons/Toast/ErrorMessage";
import { USER_TYPES } from "../../../../../Bilateral/LedgerPageDashboardActive/LedgerPageDashboardActive";
import {
  handleAcceptT_C,
  handleInitEditT_C,
  handleRejectAllT_C,
} from "../../../../../../Services/BuySell/Creation";
import {
  bilateralContractCreate,
  updateContract,
} from "../../../../../../Services/Payments/PaymentApi";

// Module-level hook — moved out of component to avoid hook-inside-hook anti-pattern
const useTransformToPayload = () => {
  const normalizeTime = useCallback(
    (time) => (time === "24:00" ? "23:59" : time),
    [],
  );

  return useCallback(
    (finalValues, user, wholeData, listingId, selectedBids) => {
      const bilateralConnections = [];
      const paymentDetailsMap = new Map();
      const processedSlots = new Set();

      const isSlotSelected = (selfSlotId, matchSlotId, bidId, companyName, matchIndex) => {
        return selectedBids.some((bid) => {
          const uniqueId1 = `${selfSlotId}-${matchSlotId}`;
          const uniqueId2 = `${selfSlotId}-${matchSlotId}-${matchIndex}`;
          const stableMatch =
            bid.selfRequisitionIndex == selfSlotId &&
            bid.consumerGeneratorBidId == bidId &&
            bid.company === companyName;
          return bid.uniqueId === uniqueId1 || bid.uniqueId === uniqueId2 || stableMatch;
        });
      };

      Object.keys(finalValues).forEach((selfReqIndex) => {
        const requisitionData = finalValues[selfReqIndex];

        Object.keys(requisitionData).forEach((bidId) => {
          const counterpartyData = requisitionData[bidId];
          const matchData = wholeData
            ?.find((item) => item.matches?.some((match) => match.selfSlot.id == selfReqIndex))
            ?.matches?.find((match) => match.selfSlot.id == selfReqIndex);

          if (!matchData) return;

          let counterparty;
          if (user === USER_TYPES.CONSUMER) {
            counterparty = matchData?.generators?.find((gen) => gen.bidId == bidId);
          } else if (user === USER_TYPES.GENERATOR) {
            counterparty = matchData?.consumers?.find((con) => con.bidId == bidId);
          }

          if (!counterparty || counterparty.initiatorType) return;

          if (matchData && counterparty) {
            let reqCounter = 1;
            let hasSelectedSlot = false;

            counterparty.matchSlots?.forEach((matchSlot, slotIndex) => {
              if (matchSlot.finalizedConnectionValues) {
                const sourceData = matchSlot.finalizedConnectionValues || matchSlot;
                const consumerSlotId =
                  user === USER_TYPES.CONSUMER ? matchData.selfSlot.id : matchSlot.id;
                const generatorSlotId =
                  user === USER_TYPES.CONSUMER ? matchSlot.id : matchData.selfSlot.id;

                const connection = {
                  id: sourceData.id,
                  consumerGeneratorBidId: parseInt(bidId),
                  consumerSlotId,
                  generatorSlotId,
                  dateStartAt:
                    counterpartyData[`req${reqCounter}_startDate`] || sourceData.dateStartAt,
                  dateEndAt:
                    counterpartyData[`req${reqCounter}_endDate`] || sourceData.dateEndAt,
                  slotStartAt: normalizeTime(
                    counterpartyData[`req${reqCounter}_startTime`] || sourceData.slotStartAt,
                  ),
                  slotEndAt: normalizeTime(
                    counterpartyData[`req${reqCounter}_endTime`] || sourceData.slotEndAt,
                  ),
                  quantity: parseFloat(
                    counterpartyData[`req${reqCounter}_quantity`] || sourceData.quantity || 0,
                  ),
                  energyRateType:
                    counterpartyData[`req${reqCounter}_energyRateStr`] ||
                    sourceData.energyRateType ||
                    "",
                  amount:
                    (isNaN(counterpartyData[`req${reqCounter}_energyRate`])
                      ? counterpartyData[`req${reqCounter}_energyRate`]?.split("+")?.[1] !== undefined
                        ? Number(counterpartyData[`req${reqCounter}_energyRate`]?.split("+")?.[1])
                        : Number(counterpartyData[`req${reqCounter}_energyRate`]?.split("-")?.[1])
                      : Number(counterpartyData[`req${reqCounter}_energyRate`])) ??
                    matchSlot?.landingCost ??
                    matchSlot?.sellingCost ??
                    0,
                  purchaseEnergy:
                    Number(counterpartyData[`req${reqCounter}_quantity`]) ||
                    sourceData.quantity ||
                    0,
                };

                bilateralConnections.push(connection);
                hasSelectedSlot = true;
                reqCounter++;
              } else {
                const consumerSlotId =
                  user === USER_TYPES.CONSUMER ? matchData.selfSlot.id : matchSlot.id;
                const generatorSlotId =
                  user === USER_TYPES.CONSUMER ? matchSlot.id : matchData.selfSlot.id;

                const matchIndex =
                  wholeData?.findIndex((item) =>
                    item.matches?.some((m) => m.selfSlot.id == selfReqIndex),
                  ) || 0;

                if (
                  !isSlotSelected(
                    matchData.selfSlot.id,
                    matchSlot.id,
                    bidId,
                    counterparty.companyName,
                    matchIndex,
                  )
                ) {
                  return;
                }

                const slotKey = `${consumerSlotId}-${generatorSlotId}-${bidId}`;
                if (processedSlots.has(slotKey)) return;
                processedSlots.add(slotKey);

                const connection = {
                  consumerGeneratorBidId: parseInt(bidId),
                  consumerSlotId,
                  generatorSlotId,
                  dateStartAt:
                    counterpartyData[`req${reqCounter}_startDate`] || matchSlot.dateStartAt,
                  dateEndAt:
                    counterpartyData[`req${reqCounter}_endDate`] || matchSlot.dateEndAt,
                  slotStartAt: normalizeTime(
                    counterpartyData[`req${reqCounter}_startTime`] || matchSlot.slotStartAt,
                  ),
                  slotEndAt: normalizeTime(
                    counterpartyData[`req${reqCounter}_endTime`] || matchSlot.slotEndAt,
                  ),
                  quantity: parseFloat(counterpartyData[`req${reqCounter}_quantity`] || 0),
                  amount:
                    (isNaN(counterpartyData[`req${reqCounter}_energyRate`])
                      ? counterpartyData[`req${reqCounter}_energyRate`]?.split("+")?.[1] !== undefined
                        ? Number(counterpartyData[`req${reqCounter}_energyRate`]?.split("+")?.[1])
                        : Number(counterpartyData[`req${reqCounter}_energyRate`]?.split("-")?.[1])
                      : Number(counterpartyData[`req${reqCounter}_energyRate`])) ??
                    matchSlot?.landingCost ??
                    matchSlot?.sellingCost ??
                    0,
                  purchaseEnergy: matchSlot?.purchaseEnergy || matchSlot?.sellEnergy || 0,
                  energyRateType: counterpartyData[`req${reqCounter}_energyRateStr`] || "",
                };
                bilateralConnections.push(connection);
                hasSelectedSlot = true;
                reqCounter++;
              }
            });

            if (hasSelectedSlot && !paymentDetailsMap.has(bidId)) {
              paymentDetailsMap.set(bidId, {
                consumerGeneratorBidId: parseInt(bidId),
                paymentSecurity: parseFloat(counterpartyData.paymentSecurity || 0),
                initialMargin: parseFloat(counterpartyData.initialMargin || 0),
                emd: parseFloat(counterpartyData.emd || 0),
              });
            }
          }
        });
      });

      return {
        userType: user,
        bilateralConnections,
        paymentDetails: Array.from(paymentDetailsMap.values()),
        capacityId: listingId,
      };
    },
    [normalizeTime],
  );
};

const useTermsCondition = ({ open, handleClose, isEditTerms = false, listingId, type }) => {
  // ─── State ───────────────────────────────────────────────────────────────
  const [uniqueSelfReq, setUniqueSelfReq] = useState([]);
  const [selectedSelfReq, setSelectedSelfReq] = useState(null);
  const [finalValues, setFinalValues] = useState({});
  const [sharedPaymentValues, setSharedPaymentValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [conformOpen, setConformOpen] = useState(false);
  const [dataWithIndex, setDataWithIndex] = useState([]);
  const [collapsedCompanies, setCollapsedCompanies] = useState(new Set());
  const [collapsedRequisitions, setCollapsedRequisitions] = useState(new Set());
  const [wholeData, setWholeData] = useState([]);
  const [rejectAllTermsConfirm, setRejectAllTermsConfirm] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [selectedBids, setSelectedBids] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [chargeModal, setChargeModal] = useState(false);
  const [chargeDetails, setChargeDetails] = useState();
  const [rejectContract, setRejectContract] = useState([]);

  // ─── Redux ────────────────────────────────────────────────────────────────
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selfEntityId = useSelector(
    (state) =>
      state?.persistedReducer?.user?.activeGeneratorId ||
      state?.persistedReducer?.user?.activeConsumerId,
  );
  const activeRole = useSelector((state) => state?.persistedReducer?.userType);
  const activeConnectionData = useSelector((state) => state?.CapacitySlice?.activeConnection);
  const { listingType } = useSelector((state) => state?.CapacitySlice?.activeMain);
  const mainScreenMatchData = useSelector(
    (state) => state?.CapacitySlice?.mainScreenActiveConnection,
  );
  const selfCapacityById = useSelector((state) => state?.CapacitySlice?.activeMain);
  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole,
  )?.toLowerCase();
  const consumerList = useSelector(
    (state) => state?.CapacitySlice?.fetchSideBarList?.consumers,
  );
  const generatorList = useSelector(
    (state) => state?.CapacitySlice?.fetchSideBarList?.generators,
  );
  const consumerGeneratorId = useSelector((state) => {
    return (
      state?.persistedReducer?.user?.activeGeneratorId ||
      state?.persistedReducer?.user?.activeConsumerId
    );
  });

  // ─── Derived ─────────────────────────────────────────────────────────────
  const isConsumer = user === "consumer";
  const isGenerator = user === "generator";

  // ─── Effects ─────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
  }, [dispatch, listingId]);

  useEffect(() => {
    if (listingId) {
      dispatch(fetchActiveConnection({ curr: 0, capId: listingId, isTermsAccepted: null }));
    }
  }, [dispatch, listingId]);

  useEffect(() => {
    if (!activeConnectionData || !Array.isArray(activeConnectionData)) return;

    const cloned = JSON.parse(JSON.stringify(activeConnectionData));
    const isAcceptFlow = type === "accept";

    cloned?.forEach((item) => {
      if (!item.matches) return;

      item.matches = item.matches
        .map((match) => {
          const partyKey = match.generators ? "generators" : "consumers";
          if (!match[partyKey]) return null;

          match[partyKey] = match[partyKey]
            .map((party) => {
              if (isEditTerms) {
                const isInitiated = party.status === "TERMS_INITIATED";
                const initiatedByMe =
                  party.initiatorType && party.initiatorType.toLowerCase() === user;
                if (!(isInitiated && initiatedByMe)) return null;
              } else if (!isEditTerms) {
                if (isAcceptFlow) {
                  const isInitiated = party.status === "TERMS_INITIATED";
                  const initiatedByCounterparty =
                    party.initiatorType && party.initiatorType.toLowerCase() !== user;
                  if (!(isInitiated && initiatedByCounterparty)) return null;
                } else {
                  const isApproved = party.status === "APPROVED";
                  const isCounterpartyInitiated =
                    party.status === "TERMS_INITIATED" &&
                    party.initiatorType &&
                    party.initiatorType.toLowerCase() !== user;
                  if (!isApproved && !isCounterpartyInitiated) return null;
                }
              }

              const slots = party.matchSlots || [];
              const filteredSlots = isAcceptFlow
                ? slots.filter((slot) => slot.finalizedConnectionValues)
                : slots;

              if (!filteredSlots.length) return null;
              return { ...party, matchSlots: filteredSlots };
            })
            .filter(Boolean);

          return match[partyKey].length > 0 ? match : null;
        })
        .filter(Boolean);
    });

    const filtered = cloned.filter((item) => item.matches && item.matches.length > 0);
    setWholeData(filtered);
  }, [activeConnectionData, type, isEditTerms, user]);

  // ─── Data helpers ────────────────────────────────────────────────────────
  function getMaxQuantity() {
    if (activeRole === "CONSUMER") {
      const consumer = consumerList?.find((c) => c.consumerId === consumerGeneratorId);
      return consumer?.maxCapDrawl;
    }
    if (activeRole === "GENERATOR") {
      const generator = generatorList?.find((g) => g.generatorId === consumerGeneratorId);
      return generator?.maxInjCap;
    }
  }

  const getFinalizedId = useCallback(() => {
    if (isEditTerms && mainScreenMatchData) {
      let ids = [];
      const accessKey = user === "consumer" ? "generatorSlot" : "consumerSlot";
      for (let i = 0; i < mainScreenMatchData.length; i++) {
        for (let j = 0; j < mainScreenMatchData[i].bilateralConnections.length; j++) {
          ids.push(mainScreenMatchData[i].bilateralConnections[j][accessKey].id);
        }
      }
      return ids;
    }
  }, [isEditTerms, mainScreenMatchData, user]);

  const getEditData = useCallback(() => {
    if (!isEditTerms || getFinalizedId().length === 0) return [];

    const accessKey = user === "consumer" ? "generators" : "consumers";
    const finalizedIds = getFinalizedId();
    const copiedWholeData = JSON.parse(JSON.stringify(wholeData));

    return copiedWholeData
      .map((item) => {
        const cleanedMatches = item.matches
          .map((match) => {
            const cleanedEntities = match[accessKey]
              .map((entity) => {
                const cleanedSlots = entity.matchSlots.filter(
                  (slot) =>
                    finalizedIds.includes(slot.id) &&
                    slot.finalizedConnectionValues?.status !== "ACCEPTED",
                );
                return cleanedSlots.length > 0 ? { ...entity, matchSlots: cleanedSlots } : null;
              })
              .filter(Boolean);

            return cleanedEntities.length > 0
              ? { ...match, [accessKey]: cleanedEntities }
              : null;
          })
          .filter(Boolean);

        return cleanedMatches.length > 0 ? { ...item, matches: cleanedMatches } : null;
      })
      .filter(Boolean);
  }, [getFinalizedId, isEditTerms, user, wholeData]);

  const finalData = useCallback(() => {
    return isEditTerms ? getEditData() : wholeData;
  }, [wholeData, getEditData, isEditTerms]);

  const getCounterparties = useCallback(
    (match) => (isConsumer ? match.generators : match.consumers),
    [isConsumer],
  );

  const getCapacityData = useCallback(() => {
    if (isConsumer) return dataWithIndex[0]?.capacityConsumer || {};
    if (isGenerator) return dataWithIndex[0]?.capacityGenerator || {};
    return {};
  }, [dataWithIndex, isConsumer, isGenerator]);

  // ─── Effects: derived data ───────────────────────────────────────────────
  useEffect(() => {
    let idToIndexMap = {};
    if (wholeData && Array.isArray(wholeData)) {
      wholeData.forEach((item) => {
        item.matches?.forEach((match, idx) => {
          idToIndexMap[match.selfSlot.id] = idx + 1;
        });
      });
    }

    const addIndexToMatches = (arr) =>
      arr?.map((item) => ({
        ...item,
        matches:
          item.matches?.map((match) => ({
            ...match,
            selfSlot: {
              ...match.selfSlot,
              index: idToIndexMap[match.selfSlot.id] || 1,
            },
          })) ?? [],
      }));

    const dataToProcess = finalData();
    if (dataToProcess && Array.isArray(dataToProcess)) {
      setDataWithIndex(addIndexToMatches(dataToProcess));
    }
  }, [wholeData, finalData]);

  useEffect(() => {
    if (!dataWithIndex?.[0]?.matches) return;
    const allSelfSlots = dataWithIndex[0].matches.map((match, i) => ({
      id: i,
      reqId: match.selfSlot.index,
      selfRequisitionIndex: match.selfSlot.id,
    }));
    const unique = allSelfSlots.filter(
      (item, idx, arr) =>
        arr.findIndex((i) => i.selfRequisitionIndex === item.selfRequisitionIndex) === idx,
    );
    setUniqueSelfReq(unique);
    setSelectedSelfReq(unique[0]);
  }, [dataWithIndex]);

  useEffect(() => {
    if (!dataWithIndex?.[0]?.matches) return;

    let newSelectedBids = [...selectedBids];
    let newCollapsedCompanies = new Set(collapsedCompanies);
    let newCollapsedRequisitions = new Set(collapsedRequisitions);

    dataWithIndex[0].matches.forEach((match, matchIndex) => {
      const selfSlot = match.selfSlot;
      const counterparties = getCounterparties(match);

      counterparties.forEach((item) => {
        if (item.status === "TERMS_INITIATED") {
          (item.matchSlots || []).forEach((slot, slotIndex) => {
            const uniqueId = `${selfSlot?.id}-${slot.id}`;
            if (
              !newSelectedBids.some(
                (bid) => bid.id === slot.id && bid.selfSlotId === selfSlot?.id,
              )
            ) {
              newSelectedBids.push({
                ...slot,
                company: item.companyName,
                name: item.generatorName || item.consumerName,
                consumerGeneratorBidId: item.bidId,
                counterpartyEntityId: item?.consumerId || item?.generatorId,
                traderId: item?.traderId,
                selected: true,
                selfRequisitionIndex: `${matchIndex}-${slotIndex}`,
                uniqueId,
                stableId: `${matchIndex}-${slotIndex}-${item.bidId}-${item.companyName}`,
                selfSlotId: selfSlot?.id,
                matchIndex,
              });
            }
            newCollapsedRequisitions.delete(`${item.bidId}-${matchIndex}-${slot.id}`);
          });
          newCollapsedCompanies.delete(`${item.bidId}-${matchIndex}`);
        }
      });
    });

    if (
      newSelectedBids.length !== selectedBids.length ||
      newCollapsedCompanies.size !== collapsedCompanies.size ||
      newCollapsedRequisitions.size !== collapsedRequisitions.size
    ) {
      setSelectedBids(newSelectedBids);
      setCollapsedCompanies(newCollapsedCompanies);
      setCollapsedRequisitions(newCollapsedRequisitions);
    }
    // eslint-disable-next-line
  }, [dataWithIndex]);

  // ─── Value helpers ───────────────────────────────────────────────────────
  const getDefaultValues = useCallback(
    (matchSlotsCount, selfSlot, matchSlots = [], counterparty = null) => {
      const capacityData = getCapacityData();
      const finalizedPaymentDetails = counterparty?.finalizedPaymentDetails;
      const isDisabled =
        (user === "consumer" && counterparty?.initiatorType === "generator") ||
        (user === "generator" && counterparty?.initiatorType === "consumer");

      let defaults = {
        paymentSecurity:
          finalizedPaymentDetails?.paymentSecurity ??
          selfSlot?.paymentSecurity ??
          capacityData.paymentSecurity ??
          "",
        initialMargin:
          finalizedPaymentDetails?.initialMargin ??
          selfSlot?.initialMargin ??
          capacityData.initialMargin ??
          "",
        emd:
          finalizedPaymentDetails?.emd ?? selfSlot?.emd ?? capacityData.emd ?? "",
      };

      for (let i = 1; i <= matchSlotsCount; i++) {
        const matchSlot = matchSlots[i - 1];
        const finalizedValues = matchSlot?.finalizedConnectionValues;

        if (isEditTerms && finalizedValues) {
          defaults[`req${i}_energyRate`] = finalizedValues.amount ?? "";
          defaults[`req${i}_energyRateStr`] = finalizedValues.energyRateType ?? "";
          defaults[`req${i}_quantity`] = finalizedValues.quantity ?? "";
          defaults[`req${i}_startDate`] = finalizedValues.dateStartAt ?? "";
          defaults[`req${i}_endDate`] = finalizedValues.dateEndAt ?? "";
          defaults[`req${i}_startTime`] = finalizedValues.slotStartAt ?? "";
          defaults[`req${i}_endTime`] = finalizedValues.slotEndAt ?? "";
        } else if (!isDisabled) {
          defaults[`req${i}_energyRate`] =
            finalizedValues?.amount ?? selfSlot?.landingCost ?? selfSlot?.sellingCost ?? "";
          defaults[`req${i}_energyRateStr`] = selfSlot?.energyRateType ?? "";
          defaults[`req${i}_quantity`] =
            finalizedValues?.quantity ?? selfSlot?.sellEnergy ?? selfSlot?.purchaseEnergy ?? "";
          defaults[`req${i}_startDate`] = finalizedValues?.dateStartAt ?? selfSlot?.dateStartAt ?? "";
          defaults[`req${i}_endDate`] = finalizedValues?.dateEndAt ?? selfSlot?.dateEndAt ?? "";
          defaults[`req${i}_startTime`] = finalizedValues?.slotStartAt ?? selfSlot?.slotStartAt ?? "";
          defaults[`req${i}_endTime`] = finalizedValues?.slotEndAt ?? selfSlot?.slotEndAt ?? "";
        } else {
          defaults[`req${i}_energyRate`] =
            finalizedValues?.amount ?? matchSlot?.sellingCost ?? matchSlot?.landingCost ?? "";
          defaults[`req${i}_energyRateStr`] =
            finalizedValues?.energyRateType ?? matchSlot?.energyRateType ?? "";
          defaults[`req${i}_quantity`] =
            finalizedValues?.quantity ?? matchSlot?.sellEnergy ?? matchSlot?.purchaseEnergy ?? "";
          defaults[`req${i}_startDate`] = finalizedValues?.dateStartAt ?? matchSlot?.dateStartAt ?? "";
          defaults[`req${i}_endDate`] = finalizedValues?.dateEndAt ?? matchSlot?.dateEndAt ?? "";
          defaults[`req${i}_startTime`] = finalizedValues?.slotStartAt ?? matchSlot?.slotStartAt ?? "";
          defaults[`req${i}_endTime`] = finalizedValues?.slotEndAt ?? matchSlot?.slotEndAt ?? "";
        }
      }
      return defaults;
    },
    [getCapacityData, isEditTerms, user],
  );

  const getCurrentValues = useCallback(
    (counterparty, matchSlotsCount) => {
      if (!selectedSelfReq || !counterparty) return {};
      const selfSlot = dataWithIndex[0]?.matches?.[selectedSelfReq.id]?.selfSlot || {};
      const matchSlots = counterparty.matchSlots || [];
      const defaultValues = getDefaultValues(matchSlotsCount, selfSlot, matchSlots, counterparty);
      const storedValues =
        finalValues?.[selectedSelfReq.selfRequisitionIndex]?.[counterparty.bidId] || {};
      const sharedValues = sharedPaymentValues[counterparty.bidId] || {};

      if (type === "accept") {
        let finalizedValues = {};
        matchSlots.forEach((matchSlot, idx) => {
          if (matchSlot.finalizedConnectionValues) {
            const finalized = matchSlot.finalizedConnectionValues;
            finalizedValues[`req${idx + 1}_energyRate`] = finalized.amount ?? "";
            finalizedValues[`req${idx + 1}_energyRateStr`] = finalized.energyRateType ?? "";
            finalizedValues[`req${idx + 1}_quantity`] = finalized.quantity ?? "";
            finalizedValues[`req${idx + 1}_startDate`] = finalized.dateStartAt ?? "";
            finalizedValues[`req${idx + 1}_endDate`] = finalized.dateEndAt ?? "";
            finalizedValues[`req${idx + 1}_startTime`] = finalized.slotStartAt ?? "";
            finalizedValues[`req${idx + 1}_endTime`] = finalized.slotEndAt ?? "";
          }
        });

        return {
          ...defaultValues,
          ...finalizedValues,
          ...storedValues,
          paymentSecurity:
            sharedValues.paymentSecurity ??
            counterparty.finalizedPaymentDetails?.paymentSecurity ??
            defaultValues.paymentSecurity,
          initialMargin:
            sharedValues.initialMargin ??
            counterparty.finalizedPaymentDetails?.initialMargin ??
            defaultValues.initialMargin,
          emd:
            sharedValues.emd ??
            counterparty.finalizedPaymentDetails?.emd ??
            defaultValues.emd,
        };
      }

      return {
        ...defaultValues,
        ...storedValues,
        paymentSecurity: sharedValues.paymentSecurity ?? defaultValues.paymentSecurity,
        initialMargin: sharedValues.initialMargin ?? defaultValues.initialMargin,
        emd: sharedValues.emd ?? defaultValues.emd,
      };
    },
    [
      selectedSelfReq,
      dataWithIndex,
      finalValues,
      sharedPaymentValues,
      getDefaultValues,
      type,
    ],
  );

  // ─── Payload helpers ─────────────────────────────────────────────────────
  const transformToPayload = useTransformToPayload();

  const checkPayloadType = useCallback(
    (finalValues) => {
      if (!wholeData[0]?.matches || !Array.isArray(wholeData[0].matches)) {
        return { type: "finalize", hasFinalize: false, hasAccept: false };
      }

      const matches = wholeData[0].matches;
      const counterpartyKey = user === USER_TYPES.GENERATOR ? "consumers" : "generators";

      let hasWithInitiatorType = false;
      let hasWithoutInitiatorType = false;

      matches.forEach((match, matchIndex) => {
        const counterparties = match[counterpartyKey] || [];
        counterparties.forEach((counterparty) => {
          const hasSelectedSlot = counterparty.matchSlots?.some((slot) =>
            selectedBids.some(
              (bid) =>
                bid.id === slot.id &&
                bid.consumerGeneratorBidId == counterparty.bidId &&
                bid.matchIndex === matchIndex,
            ),
          );
          if (!hasSelectedSlot) return;
          if (counterparty.initiatorType) {
            hasWithInitiatorType = true;
          } else {
            hasWithoutInitiatorType = true;
          }
        });
      });

      if (hasWithInitiatorType && hasWithoutInitiatorType) {
        return { type: "both", hasFinalize: true, hasAccept: true };
      } else if (hasWithInitiatorType) {
        return { type: "accept", hasFinalize: false, hasAccept: true };
      } else if (hasWithoutInitiatorType) {
        return { type: "finalize", hasFinalize: true, hasAccept: false };
      } else {
        return { type: "finalize", hasFinalize: false, hasAccept: false };
      }
    },
    [wholeData, user, selectedBids],
  );

  const getAllFinalizedConnectionIds = useCallback(
    (onGoingData, finalValues) => {
      const ids = [];
      if (!onGoingData?.matches) return ids;

      const isSlotSelected = (selfSlotId, matchSlotId, bidId, companyName, matchIndex) => {
        return selectedBids.some((bid) => {
          const uniqueId1 = `${selfSlotId}-${matchSlotId}`;
          const uniqueId2 = `${selfSlotId}-${matchSlotId}-${matchIndex}`;
          const stableMatch =
            bid.selfRequisitionIndex == selfSlotId &&
            bid.consumerGeneratorBidId == bidId &&
            bid.company === companyName;
          return bid.uniqueId === uniqueId1 || bid.uniqueId === uniqueId2 || stableMatch;
        });
      };

      const processSlot = (slot, selfSlotId, bidId, companyName, matchIndex) => {
        if (slot.finalizedConnectionValues && slot.finalizedConnectionValues.id !== undefined) {
          if (isSlotSelected(selfSlotId, slot.id, bidId, companyName, matchIndex)) {
            ids.push(slot.finalizedConnectionValues.id);
          }
        }
      };

      onGoingData.matches.forEach((match, matchIndex) => {
        [...(match.consumers || []), ...(match.generators || [])].forEach((item) => {
          (item.matchSlots || []).forEach((slot) => {
            processSlot(slot, match.selfSlot.id, item.bidId, item.companyName, matchIndex);
          });
        });
      });

      return ids;
    },
    [selectedBids],
  );

  // ─── Validation ──────────────────────────────────────────────────────────
  const validateStartDateTimes = useCallback(
    (finalValues) => {
      if (!finalValues || !dataWithIndex?.[0]?.matches) return true;

      const listingExpDate = selfCapacityById?.listingExpDate;
      const listingExpTime = selfCapacityById?.listingExpTime;
      if (!listingExpDate || !listingExpTime) return true;

      let earliestStartDT = null;

      dataWithIndex.forEach((item) => {
        if (!item.matches) return;
        item.matches.forEach((match, matchIndex) => {
          const selfReqIndex = String(match.selfSlot.id);
          const counterparties = getCounterparties(match);
          counterparties.forEach((counterparty) => {
            const matchSlots = counterparty.matchSlots || [];
            const storedValues = finalValues?.[selfReqIndex]?.[counterparty.bidId] || {};
            matchSlots.forEach((slot, idx) => {
              const isSlotSelected = selectedBids.some(
                (bid) =>
                  bid.id === slot.id &&
                  bid.selfSlotId === match.selfSlot.id &&
                  bid.consumerGeneratorBidId == counterparty.bidId &&
                  (bid.matchIndex === matchIndex || bid.matchIndex === undefined),
              );
              if (!isSlotSelected) return;
              const startDate =
                storedValues[`req${idx + 1}_startDate`] || slot.dateStartAt;
              const startTime =
                storedValues[`req${idx + 1}_startTime`] || slot.slotStartAt;
              if (startDate && startTime) {
                const startDT = new Date(`${startDate}T${startTime}`);
                if (!earliestStartDT || startDT < earliestStartDT) {
                  earliestStartDT = startDT;
                }
              }
            });
          });
        });
      });

      return true;
    },
    [dataWithIndex, selfCapacityById, getCounterparties, selectedBids],
  );

  const getAllFilledFinalValues = useCallback(() => {
    const result = {};
    if (!Array.isArray(dataWithIndex)) return result;

    try {
      if (selectedBids.length === 0) {
        toast.error("Please select at least one requisition.");
        throw new Error("NO_BIDS_SELECTED");
      }

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const tomorrowDate = new Date(todayStart);
      tomorrowDate.setDate(todayStart.getDate() + 1);
      const upto7Date = new Date(todayStart);
      upto7Date.setDate(todayStart.getDate() + 6);
      const dayAfterTomorrow = new Date(todayStart);
      dayAfterTomorrow.setDate(todayStart.getDate() + 2);
      const next7EndMax = new Date(todayStart);
      next7EndMax.setDate(todayStart.getDate() + 6);

      const reqStageMap = {
        INTRA_DAY: "intraday",
        NEXT_DAY: "nextday",
        UPTO_SEVEN_DAYS: "upto_7",
        GREATER_THAN_SEVEN_DAYS: "greater_7",
      };
      const reqStage = reqStageMap[listingType] || null;

      const getMaxSelectedDurationDays = (match, counterparty, matchSlots, selfReqIndex, bidId, matchIndex) => {
        let maxDays = null;
        const storedValues = finalValues?.[selfReqIndex]?.[bidId] || {};
        matchSlots.forEach((slot, idx) => {
          const isSlotSelected = selectedBids.some(
            (bid) =>
              bid.id === slot.id &&
              bid.selfSlotId === match.selfSlot.id &&
              bid.consumerGeneratorBidId == bidId &&
              (bid.matchIndex === matchIndex || bid.matchIndex === undefined),
          );
          if (!isSlotSelected) return;
          const startDate = storedValues[`req${idx + 1}_startDate`] || slot.dateStartAt;
          const endDate = storedValues[`req${idx + 1}_endDate`] || slot.dateEndAt;
          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const durationDays =
              Math.max(0, Math.ceil((end - start) / (1000 * 60 * 60 * 24))) + 1;
            maxDays = maxDays === null ? durationDays : Math.max(maxDays, durationDays);
          }
        });
        return maxDays;
      };

      // Step 1: Collect energy rate types per counterparty
      const counterpartyEnergyTypes = {};
      const counterpartyNames = {};

      dataWithIndex.forEach((item) => {
        if (!item.matches) return;
        item.matches.forEach((match, matchIndex) => {
          const selfReqIndex = String(match.selfSlot.id);
          const counterparties = getCounterparties(match);
          counterparties.forEach((counterparty) => {
            const bidId = String(counterparty.bidId);
            const matchSlots = counterparty.matchSlots || [];
            const hasSelectedSlots = matchSlots.some((slot) =>
              selectedBids.some(
                (bid) =>
                  bid.id === slot.id &&
                  bid.selfSlotId === match.selfSlot.id &&
                  bid.consumerGeneratorBidId == bidId &&
                  (bid.matchIndex === matchIndex || bid.matchIndex === undefined),
              ),
            );
            if (!hasSelectedSlots) return;

            counterpartyNames[bidId] = counterparty.companyName;
            const selfSlot = match.selfSlot;
            const defaultValues = getDefaultValues(matchSlots.length, selfSlot, matchSlots, counterparty);
            const storedValues = finalValues?.[selfReqIndex]?.[bidId] || {};
            const sharedValues = sharedPaymentValues[bidId] || {};

            if (!counterpartyEnergyTypes[bidId]) counterpartyEnergyTypes[bidId] = [];

            for (let idx = 0; idx < matchSlots.length; idx++) {
              const matchSlot = matchSlots[idx];
              const isSlotSelected = selectedBids.some(
                (bid) =>
                  bid.id === matchSlot.id &&
                  bid.selfSlotId === match.selfSlot.id &&
                  bid.consumerGeneratorBidId == bidId &&
                  (bid.matchIndex === matchIndex || bid.matchIndex === undefined),
              );
              if (!isSlotSelected) continue;

              const energyRate =
                storedValues[`req${idx + 1}_energyRate`] ??
                defaultValues[`req${idx + 1}_energyRate`];
              const energyRateType =
                storedValues[`req${idx + 1}_energyRateStr`] ??
                defaultValues[`req${idx + 1}_energyRateStr`];

              if (energyRate !== "" && energyRate !== undefined && energyRate !== null) {
                if (isNaN(Number(energyRate))) {
                  toast.error(
                    `Invalid energy rate value "${energyRate}" in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}. Please enter a valid number.`,
                  );
                  throw new Error("INVALID_ENERGY_RATE_VALUE");
                }
              }

              if (energyRateType === "DAM_PLUS" || energyRateType === "DAM_MINUS") {
                counterpartyEnergyTypes[bidId].push("DAM");
              } else if (energyRateType === "FIXED") {
                counterpartyEnergyTypes[bidId].push("FIXED");
              } else {
                counterpartyEnergyTypes[bidId].push("OTHER");
              }
            }
          });
        });
      });

      // Step 2: Validate consistent energy rate type per counterparty
      for (const [bidId, typesArr] of Object.entries(counterpartyEnergyTypes)) {
        const uniqueTypes = [...new Set(typesArr)];
        if (uniqueTypes.length > 1) {
          toast.error(
            `All Energy Rate values for counterparty ${counterpartyNames[bidId]} must be of the same type (all FIXED or all DAM-based) across all selected requisitions.`,
          );
          throw new Error("INCONSISTENT_ENERGY_RATE_TYPE");
        }
      }

      // Step 3: Build result and validate per-slot fields
      dataWithIndex.forEach((item) => {
        if (!item.matches) return;
        item.matches.forEach((match, matchIndex) => {
          const selfReqIndex = String(match.selfSlot.id);
          const counterparties = getCounterparties(match);

          counterparties.forEach((counterparty, i) => {
            const bidId = String(counterparty.bidId);
            const matchSlots = counterparty.matchSlots || [];
            const selfSlot = match.selfSlot;

            const hasSelectedSlots = matchSlots.some((slot) =>
              selectedBids.some(
                (bid) =>
                  bid.id === slot.id &&
                  bid.selfSlotId === match.selfSlot.id &&
                  bid.consumerGeneratorBidId == bidId &&
                  (bid.matchIndex === matchIndex || bid.matchIndex === undefined),
              ),
            );
            if (!hasSelectedSlots) return;

            const matchSlotsCount = matchSlots.length;
            const defaultValues = getDefaultValues(matchSlotsCount, selfSlot, matchSlots, counterparty);
            const storedValues = finalValues?.[selfReqIndex]?.[bidId] || {};
            const sharedValues = sharedPaymentValues[bidId] || {};

            const paymentSecurity = sharedValues.paymentSecurity ?? defaultValues.paymentSecurity;
            const initialMargin = sharedValues.initialMargin ?? defaultValues.initialMargin;
            const emd = sharedValues.emd ?? defaultValues.emd;

            if (
              paymentSecurity === "" || paymentSecurity === undefined ||
              initialMargin === "" || initialMargin === undefined ||
              emd === "" || emd === undefined
            ) {
              toast.error(
                `All payment fields (Payment Security, Initial Margin, EMD) are required for counterparty ${counterparty.companyName}.`,
              );
              throw new Error("EMPTY_PAYMENT_FIELD");
            }

            for (let idx = 0; idx < matchSlots.length; idx++) {
              const matchSlot = matchSlots[idx];
              const isSlotSelected = selectedBids.some(
                (bid) =>
                  bid.id === matchSlot.id &&
                  bid.selfSlotId === match.selfSlot.id &&
                  bid.consumerGeneratorBidId == bidId &&
                  (bid.matchIndex === matchIndex || bid.matchIndex === undefined),
              );
              if (!isSlotSelected) continue;

              const energyRate =
                storedValues[`req${idx + 1}_energyRate`] ?? defaultValues[`req${idx + 1}_energyRate`];
              const energyRateType =
                storedValues[`req${idx + 1}_energyRateStr`] ?? defaultValues[`req${idx + 1}_energyRateStr`];
              const quantity =
                storedValues[`req${idx + 1}_quantity`] ?? defaultValues[`req${idx + 1}_quantity`];
              const startDate =
                storedValues[`req${idx + 1}_startDate`] || defaultValues[`req${idx + 1}_startDate`];
              const endDate =
                storedValues[`req${idx + 1}_endDate`] || defaultValues[`req${idx + 1}_endDate`];
              const startTime =
                storedValues[`req${idx + 1}_startTime`] || defaultValues[`req${idx + 1}_startTime`];
              const endTime =
                storedValues[`req${idx + 1}_endTime`] || defaultValues[`req${idx + 1}_endTime`];

              const isEnergyRateInvalid =
                energyRateType === "FIXED"
                  ? energyRate === "" || energyRate === undefined || energyRate === null || Number(energyRate) === 0
                  : energyRate === "" || energyRate === undefined || energyRate === null;

              if (
                isEnergyRateInvalid ||
                quantity === "" || quantity === undefined ||
                startDate === "" || startDate === undefined ||
                endDate === "" || endDate === undefined ||
                startTime === "" || startTime === undefined ||
                endTime === "" || endTime === undefined
              ) {
                if (energyRateType === "FIXED" && Number(energyRate) === 0) {
                  toast.error(
                    `Energy Rate cannot be zero for FIXED rate type in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                  );
                  throw new Error("ZERO_ENERGY_RATE_FIXED");
                }
                toast.error(
                  `All fields are required in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                );
                throw new Error("EMPTY_FIELD");
              }

              const maxQuantity = getMaxQuantity();
              const quantityValue = parseFloat(quantity);

              if (isNaN(quantityValue) || quantityValue <= 0) {
                toast.error(
                  `Quantity must be greater than zero in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                );
                throw new Error("INVALID_QUANTITY_ZERO");
              }

              if (maxQuantity && quantityValue > maxQuantity) {
                toast.error(
                  `Quantity (${quantityValue} MW) cannot exceed maximum capacity of ${maxQuantity} MW in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                );
                throw new Error("QUANTITY_EXCEEDS_MAX");
              }

              if (startDate && endDate) {
                const startDateObj = new Date(startDate);
                const endDateObj = new Date(endDate);
                if (startDateObj > endDateObj) {
                  toast.error(
                    `Start Date cannot be greater than End Date in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                  );
                  throw new Error("INVALID_DATE");
                }
              }

              if (startTime && endTime && startTime > endTime) {
                toast.error(
                  `Start Time cannot be greater than End Time in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                );
                throw new Error("INVALID_TIME");
              }

              if (startDate && reqStage) {
                const startDateObj = new Date(startDate);
                startDateObj.setHours(0, 0, 0, 0);
                if (reqStage === "intraday" && startDateObj.getTime() !== todayStart.getTime()) {
                  toast.error(`Start Date must be today for Intraday in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`);
                  throw new Error("INVALID_START_DATE_INTRADAY");
                } else if (reqStage === "nextday" && startDateObj.getTime() !== tomorrowDate.getTime()) {
                  toast.error(`Start Date must be tomorrow for Next Day in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`);
                  throw new Error("INVALID_START_DATE_NEXTDAY");
                } else if (reqStage === "upto_7" && (startDateObj < todayStart || startDateObj > upto7Date)) {
                  toast.error(`Start Date must be within next 7 days in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`);
                  throw new Error("INVALID_START_DATE_UPTO7");
                } else if (reqStage === "greater_7" && startDateObj < todayStart) {
                  toast.error(`Start Date must be today or later for Custom in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`);
                  throw new Error("INVALID_START_DATE_CUSTOM");
                }
              }

              if (endDate && reqStage) {
                const endDateObj = new Date(endDate);
                endDateObj.setHours(0, 0, 0, 0);
                if (reqStage === "intraday" && endDateObj.getTime() !== todayStart.getTime()) {
                  toast.error(`End Date must be today for Intraday in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`);
                  throw new Error("INVALID_END_DATE_INTRADAY");
                } else if (reqStage === "nextday" && endDateObj.getTime() !== tomorrowDate.getTime()) {
                  toast.error(`End Date must be tomorrow for Next Day in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`);
                  throw new Error("INVALID_END_DATE_NEXTDAY");
                } else if (reqStage === "upto_7" && (endDateObj < dayAfterTomorrow || endDateObj > next7EndMax)) {
                  toast.error(`End Date must be between day after tomorrow and 6 days from today in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`);
                  throw new Error("INVALID_END_DATE_UPTO7");
                }
              }

              if (startDate && startTime) {
                const now = new Date();
                const minAllowed = new Date(now.getTime() + 3 * 60 * 60 * 1000);
                const startDateObj = new Date(startDate);
                const [hr, min] = startTime.split(":").map(Number);
                startDateObj.setHours(hr || 0, min || 0, 0, 0);
                if (startDateObj < minAllowed) {
                  toast.error(`Start Time should be at least 3 hours from current time in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`);
                  throw new Error("START_TIME_TOO_EARLY");
                }
              }

              if (startDate && endDate && startTime && endTime) {
                const startDateTime = new Date(`${startDate}T${startTime}`);
                const endDateTime = new Date(`${endDate}T${endTime}`);
                if (startDateTime >= endDateTime) {
                  toast.error(
                    `Start Date & Time must be before End Date & Time in Requisition ${match.selfSlot.index} for counterparty ${counterparty.companyName}.`,
                  );
                  throw new Error("INVALID_DATE_TIME");
                }
              }
            }

            if (!result[selfReqIndex]) result[selfReqIndex] = {};
            result[selfReqIndex][bidId] = {
              ...defaultValues,
              ...storedValues,
              id: matchSlots[i]?.id || "",
              paymentSecurity: sharedValues.paymentSecurity ?? defaultValues.paymentSecurity,
              initialMargin: sharedValues.initialMargin ?? defaultValues.initialMargin,
              emd: sharedValues.emd ?? defaultValues.emd,
            };
          });
        });
      });
    } catch (err) {
      console.warn("Validation stopped submission:", err.message);
      return null;
    }

    return result;
  }, [
    dataWithIndex,
    selectedBids,
    getCounterparties,
    getDefaultValues,
    finalValues,
    sharedPaymentValues,
    getMaxQuantity,
    listingType,
  ]);

  // ─── API handlers ─────────────────────────────────────────────────────────
  const handleInitPayment = useCallback(
    async (contracts, allCharges) => {
      if (!contracts || contracts.length === 0) return;

      const apiPromises = [];
      const isConsumer = activeRole === "CONSUMER";
      const selfId = isConsumer
        ? contracts[0]?.capacityConsumer?.consumerId
        : contracts[0]?.capacityGenerator?.generatorId;

      const selfChargesList = allCharges.filter((charge) =>
        isConsumer ? charge.consumerId === selfId : charge.generatorId === selfId,
      );

      contracts.forEach((contract) => {
        const currentContractId = contract.id;
        const counterId = isConsumer
          ? contract.capacityGenerator?.generatorId
          : contract.capacityConsumer?.consumerId;

        const counterChargesList = allCharges.filter((charge) =>
          isConsumer ? charge.generatorId === counterId : charge.consumerId === counterId,
        );

        if (selfChargesList.length > 0) {
          const selfPayload = selfChargesList.map((charge) => ({
            chargeId: charge.id,
            chargeValue: charge.value,
            contractId: currentContractId,
            consumerId: isConsumer ? selfId : null,
            generatorId: !isConsumer ? selfId : null,
            chargeStatus: "APPROVED",
          }));
          apiPromises.push(bilateralContractCreate(selfPayload));
        }

        if (counterChargesList.length > 0) {
          const counterPayload = counterChargesList.map((charge) => ({
            chargeId: charge.id,
            chargeValue: charge.value,
            contractId: currentContractId,
            consumerId: !isConsumer ? counterId : null,
            generatorId: isConsumer ? counterId : null,
            chargeStatus: "INITIATED",
          }));
          apiPromises.push(bilateralContractCreate(counterPayload));
        }
      });

      try {
        await Promise.all(apiPromises);
      } catch (error) {
        console.error("Error initiating payments", error);
      }
    },
    [activeRole],
  );

  const dispatchRefresh = useCallback(() => {
    dispatch(fetchActiveConnection({ curr: 0, capId: listingId, isTermsAccepted: null }));
    dispatch(fetchSelfCapacityById({ user, id: listingId }));
    dispatch(fetchSelfCapacity({ user }));
    dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
  }, [dispatch, listingId, user]);

  const handleTermsSubmit = useCallback(
    async (finalValues, charges) => {
      try {
        setLoading(true);
        const finalizeTermsPayload = transformToPayload(finalValues, user, wholeData, listingId, selectedBids);
        const payloadType = isEditTerms ? { type: "finalize" } : checkPayloadType(finalValues);
        const acceptTermsPayload = getAllFinalizedConnectionIds(wholeData[0], finalValues, selectedBids);

        let finalPayload;

        if (payloadType.type === "both") {
          finalPayload = {
            ...finalizeTermsPayload,
            consumerPaymentConfigurationId:
              mainScreenMatchData?.capacityConsumer?.paymentConfigurationId,
            generatorPaymentConfigurationId:
              mainScreenMatchData?.capacityGenerator?.paymentConfigurationId,
            connectionIds: acceptTermsPayload,
            chargesToAccept: charges
              ?.filter((ele) => ele?.bilateralContractId)
              ?.map((ele) => ({
                bilateralContractId: ele?.bilateralContractId,
                chargeId: ele?.id,
                chargeValue: ele?.value,
                consumerId: ele?.consumerId,
                generatorId: ele?.generatorId,
                chargeStatus: "APPROVED",
              })),
            chargesToInitiate: charges
              ?.filter((ele) => !ele?.bilateralContractId || ele?.entityName === "Self")
              ?.map((ele) => ({
                bilateralContractId: null,
                chargeId: ele?.id,
                chargeValue: ele?.value || 0,
                consumerId: ele?.consumerId,
                generatorId: ele?.generatorId,
                chargeStatus:
                  activeRole === "CONSUMER" && ele?.consumerId
                    ? "APPROVED"
                    : activeRole === "GENERATOR" && ele?.generatorId
                      ? "APPROVED"
                      : "INITIATED",
              })),
          };
        } else if (payloadType.type === "accept") {
          finalPayload = {
            connectionIds: acceptTermsPayload,
            capacityId: listingId,
            userType: user,
            consumerPaymentConfigurationId:
              mainScreenMatchData?.[0]?.capacityConsumer?.paymentConfigurationId,
            generatorPaymentConfigurationId:
              mainScreenMatchData?.[0]?.capacityGenerator?.paymentConfigurationId,
            chargesToAccept: charges
              ?.filter((ele) => ele?.bilateralContractId)
              ?.map((ele) => ({
                bilateralContractId: ele?.bilateralContractId,
                chargeId: ele?.id,
                chargeValue: ele?.value || 0,
                consumerId: ele?.consumerId,
                generatorId: ele?.generatorId,
                chargeStatus: "APPROVED",
              })),
          };
        } else if (payloadType.type === "finalize") {
          finalPayload = {
            ...finalizeTermsPayload,
            consumerPaymentConfigurationId:
              user === "consumer" ? selfCapacityById?.paymentConfigurationId : null,
            generatorPaymentConfigurationId:
              user === "generator" ? selfCapacityById?.paymentConfigurationId : null,
            chargesToAccept: charges
              ?.filter((ele) => ele?.bilateralContractId)
              ?.map((ele) => ({
                bilateralContractId: ele?.bilateralContractId,
                chargeId: ele?.id,
                chargeValue: ele?.value || 0,
                consumerId: ele?.consumerId,
                generatorId: ele?.generatorId,
                chargeStatus: "APPROVED",
              })),
            chargesToInitiate: charges
              ?.filter((ele) => !ele?.bilateralContractId)
              ?.map((ele) => ({
                bilateralContractId: ele?.bilateralContractId || null,
                chargeId: ele?.id,
                chargeValue: ele?.value || 0,
                consumerId: ele?.consumerId,
                generatorId: ele?.generatorId,
                chargeStatus:
                  activeRole === "CONSUMER" && ele?.consumerId
                    ? "APPROVED"
                    : activeRole === "GENERATOR" && ele?.generatorId
                      ? "APPROVED"
                      : "INITIATED",
              })),
          };
        }

        if (
          finalPayload?.bilateralConnections &&
          Array.isArray(finalPayload.bilateralConnections) &&
          finalPayload.bilateralConnections.length === 0
        ) {
          ErrorMessage("bailateralConnections cannot be empty");
          setLoading(false);
          setConformOpen(false);
          return;
        }

        let res;
        if (isEditTerms) {
          res = await handleInitEditT_C(finalPayload);
        } else {
          res = await handleAcceptT_C(finalPayload);
        }

        if (res?.data?.code === 200) {
          handleClose();
          dispatchRefresh();
          SuccessMessage(
            isEditTerms
              ? "Terms & Conditions edited successfully!"
              : "Terms & Conditions accepted successfully!",
          );
          if (window.location.pathname !== "/dashboard") navigate("/dashboard");
        } else {
          ErrorMessage("Failed to accept Terms & Conditions");
        }
      } catch (error) {
        console.error("Error submitting terms:", error);
        ErrorMessage("An error occurred while submitting");
      } finally {
        setConformOpen(false);
        setLoading(false);
      }
    },
    [
      transformToPayload, user, wholeData, listingId, selectedBids, isEditTerms,
      checkPayloadType, getAllFinalizedConnectionIds, activeRole, handleClose,
      dispatchRefresh, navigate, mainScreenMatchData, selfCapacityById,
    ],
  );

  const handleDirectTerms = useCallback(
    async (finalValues) => {
      try {
        setLoading(true);
        const finalizeTermsPayload = transformToPayload(finalValues, user, wholeData, listingId, selectedBids);
        const payloadType = isEditTerms ? { type: "finalize" } : checkPayloadType(finalValues);
        const acceptTermsPayload = getAllFinalizedConnectionIds(wholeData[0], finalValues, selectedBids);

        let finalPayload;
        if (payloadType.type === "both") {
          finalPayload = { ...finalizeTermsPayload, connectionIds: acceptTermsPayload };
        } else if (payloadType.type === "accept") {
          finalPayload = { connectionIds: acceptTermsPayload, capacityId: listingId, userType: user };
        } else if (payloadType.type === "finalize") {
          finalPayload = { ...finalizeTermsPayload };
        }

        if (
          finalPayload?.bilateralConnections &&
          Array.isArray(finalPayload.bilateralConnections) &&
          finalPayload.bilateralConnections.length === 0
        ) {
          ErrorMessage("bailateralConnections cannot be empty");
          setLoading(false);
          setConformOpen(false);
          return;
        }

        let res = await handleAcceptT_C(finalPayload);
        if (res?.data?.code === 200) {
          handleClose();
          dispatchRefresh();
          dispatch(fetchLedgerBids(listingId));
          SuccessMessage(
            isEditTerms
              ? "Terms & Conditions edited successfully!"
              : "Terms & Conditions accepted successfully!",
          );
          if (window.location.pathname !== "/dashboard") navigate("/dashboard");
        } else {
          ErrorMessage("Failed to accept Terms & Conditions");
        }
      } catch (error) {
        console.error("Error submitting terms:", error);
        ErrorMessage("An error occurred while submitting");
      } finally {
        setConformOpen(false);
        setLoading(false);
      }
    },
    [
      transformToPayload, user, wholeData, listingId, selectedBids, isEditTerms,
      checkPayloadType, getAllFinalizedConnectionIds, handleClose, dispatchRefresh,
      dispatch, navigate,
    ],
  );

  const handleChargeFinal = useCallback(
    (charges) => {
      const filledValues = getAllFilledFinalValues();
      handleTermsSubmit(filledValues, charges);
    },
    [getAllFilledFinalValues, handleTermsSubmit],
  );

  const handleDirectTermsSumbit = useCallback(
    async (contracts, chargeStatus = "APPROVED") => {
      try {
        const updatePromises =
          contracts?.map((contract) =>
            updateContract({ contractId: contract.contractId, chargeStatus }),
          ) || [];
        await Promise.all(updatePromises);
        if (chargeStatus === "APPROVED") {
          const filledValues = getAllFilledFinalValues();
          handleDirectTerms(filledValues);
        }
      } catch (error) {
        console.error("Error updating contracts:", error);
      }
    },
    [getAllFilledFinalValues, handleDirectTerms],
  );

  const handleRejectAllTermsSubmit = useCallback(
    async () => {
      const contractIds = (mainScreenMatchData || [])
        .filter(
          (item) =>
            item.status === "PENDING" &&
            item.initiatorType &&
            item.initiatorType.toLowerCase() !== user,
        )
        .map((item) => item.id);

      try {
        setRejectLoading(true);
        const res = await handleRejectAllT_C({ contractIds });
        if (res?.data?.code === 200) {
          dispatchRefresh();
          dispatch(fetchLedgerBids(listingId));
          setRejectAllTermsConfirm(false);
          SuccessMessage("Terms & Conditions rejected successfully!");
          handleClose();
          if (window.location.pathname !== "/dashboard") navigate("/dashboard");
        }
      } catch (error) {
        ErrorMessage("An error occurred while rejecting terms.");
      } finally {
        setRejectLoading(false);
      }
    },
    [dispatch, handleClose, listingId, mainScreenMatchData, navigate, user, dispatchRefresh],
  );

  // ─── UI handlers ─────────────────────────────────────────────────────────
  const handleInputChange = useCallback(
    (e, key, counterparty, matchSlotsCount) => {
      if (!selectedSelfReq || !counterparty) return;

      let value;
      if (e && typeof e === "object" && "target" in e && "value" in e.target) {
        value = e.target.value;
      } else {
        value = e instanceof Date ? format(e, "yyyy-MM-dd") : e;
      }

      if (key.includes("req")) {
        const reqIndex = key.match(/req(\d+)_/)?.[1];
        if (reqIndex) {
          const slotIndex = parseInt(reqIndex) - 1;
          const matchSlot = counterparty.matchSlots?.[slotIndex];
          if (matchSlot) {
            const selfSlot = dataWithIndex[0]?.matches?.[selectedSelfReq.id]?.selfSlot;
            const uniqueId = `${selfSlot?.id}-${matchSlot.id}`;
            const isAlreadySelected = selectedBids.some(
              (bid) => bid.uniqueId === uniqueId && bid.matchIndex === selectedSelfReq.id,
            );
            if (!isAlreadySelected) {
              const enrichedBid = {
                ...matchSlot,
                name: counterparty.generatorName || counterparty.consumerName,
                company: counterparty.companyName,
                counterpartyEntityId: counterparty?.consumerId || counterparty?.generatorId,
                traderId: counterparty?.traderId,
                consumerGeneratorBidId: counterparty.bidId,
                selected: true,
                selfRequisitionIndex: `${selectedSelfReq.id}-${slotIndex}`,
                uniqueId,
                stableId: `${selectedSelfReq.id}-${slotIndex}-${counterparty.bidId}-${counterparty.companyName}`,
                selfSlotId: selfSlot?.id,
                matchIndex: selectedSelfReq.id,
              };
              setSelectedBids((prev) => [...prev, enrichedBid]);

              const reqKey = `${counterparty.bidId}-${selectedSelfReq.id}-${matchSlot.id}`;
              const companyKey = `${counterparty.bidId}-${selectedSelfReq.id}`;
              setCollapsedRequisitions((prev) => { const s = new Set(prev); s.delete(reqKey); return s; });
              setCollapsedCompanies((prev) => { const s = new Set(prev); s.delete(companyKey); return s; });
            }
          }
        }
      }

      if (key.includes("energyRate") && !key.includes("energyRateStr")) {
        const reqIndex = key.match(/req(\d+)_energyRate/)[1];
        const currentValues = getCurrentValues(counterparty, matchSlotsCount);
        const currentEnergyRateType = currentValues[`req${reqIndex}_energyRateStr`] || "FIXED";
        setFinalValues((prev) => ({
          ...prev,
          [selectedSelfReq.selfRequisitionIndex]: {
            ...(prev[selectedSelfReq.selfRequisitionIndex] || {}),
            [counterparty.bidId]: {
              ...getCurrentValues(counterparty, matchSlotsCount),
              [`req${reqIndex}_energyRate`]: value,
              [`req${reqIndex}_energyRateStr`]: currentEnergyRateType,
            },
          },
        }));
        return;
      }

      const isPaymentField = ["paymentSecurity", "initialMargin", "emd"].includes(key);
      if (isPaymentField) {
        setSharedPaymentValues((prev) => ({
          ...prev,
          [counterparty.bidId]: { ...prev[counterparty.bidId], [key]: value },
        }));
        setFinalValues((prev) => {
          const newFinalValues = { ...prev };
          dataWithIndex.forEach((item) => {
            if (!item.matches) return;
            item.matches.forEach((match) => {
              const selfReqIndex = match.selfSlot.id;
              const counterparties = getCounterparties(match);
              if (counterparties.some((cp) => cp.bidId === counterparty.bidId)) {
                if (!newFinalValues[selfReqIndex]) newFinalValues[selfReqIndex] = {};
                if (!newFinalValues[selfReqIndex][counterparty.bidId])
                  newFinalValues[selfReqIndex][counterparty.bidId] = {};
                newFinalValues[selfReqIndex][counterparty.bidId] = {
                  ...newFinalValues[selfReqIndex][counterparty.bidId],
                  [key]: value,
                };
              }
            });
          });
          return newFinalValues;
        });
      } else {
        setFinalValues((prev) => ({
          ...prev,
          [selectedSelfReq.selfRequisitionIndex]: {
            ...(prev[selectedSelfReq.selfRequisitionIndex] || {}),
            [counterparty.bidId]: {
              ...getCurrentValues(counterparty, matchSlotsCount),
              [key]: value,
            },
          },
        }));
      }
    },
    [
      selectedSelfReq, dataWithIndex, selectedBids, getCounterparties,
      getCurrentValues,
    ],
  );

  const handleEnergyRateTypeChange = useCallback(
    (newType, counterparty, matchIndex) => {
      if (!selectedSelfReq || !counterparty) return;

      let hasOtherFixed = false;
      let hasOtherDAM = false;

      dataWithIndex.forEach((item) => {
        if (!item.matches) return;
        item.matches.forEach((match) => {
          const selfReqIndex = String(match.selfSlot.id);
          const counterparties = getCounterparties(match);
          const sameCounterparty = counterparties.find((cp) => cp.bidId === counterparty.bidId);
          if (sameCounterparty) {
            const storedValues = finalValues?.[selfReqIndex]?.[counterparty.bidId] || {};
            const matchSlotsCount = sameCounterparty.matchSlots?.length || 0;
            const selfSlot = match.selfSlot;
            const matchSlots = sameCounterparty.matchSlots || [];
            const defaultValues = getDefaultValues(matchSlotsCount, selfSlot, matchSlots, sameCounterparty);
            for (let i = 1; i <= matchSlotsCount; i++) {
              const existingType =
                storedValues[`req${i}_energyRateStr`] || defaultValues[`req${i}_energyRateStr`];
              if (existingType === "FIXED") hasOtherFixed = true;
              if (existingType === "DAM_PLUS" || existingType === "DAM_MINUS") hasOtherDAM = true;
            }
          }
        });
      });

      const isNewFixed = newType === "FIXED";
      const isNewDAM = newType === "DAM_PLUS" || newType === "DAM_MINUS";
      const needsSwitch = (isNewFixed && hasOtherDAM) || (isNewDAM && hasOtherFixed);

      if (needsSwitch) {
        setFinalValues((prev) => {
          const newFinalValues = { ...prev };
          dataWithIndex.forEach((item) => {
            if (!item.matches) return;
            item.matches.forEach((match) => {
              const selfReqIndex = String(match.selfSlot.id);
              const counterparties = getCounterparties(match);
              const sameCounterparty = counterparties.find((cp) => cp.bidId === counterparty.bidId);
              if (sameCounterparty) {
                if (!newFinalValues[selfReqIndex]) newFinalValues[selfReqIndex] = {};
                if (!newFinalValues[selfReqIndex][counterparty.bidId])
                  newFinalValues[selfReqIndex][counterparty.bidId] = {};
                const matchSlotsCount = sameCounterparty.matchSlots?.length || 0;
                for (let i = 1; i <= matchSlotsCount; i++) {
                  newFinalValues[selfReqIndex][counterparty.bidId][`req${i}_energyRate`] = "";
                  newFinalValues[selfReqIndex][counterparty.bidId][`req${i}_energyRateStr`] = newType;
                }
              }
            });
          });
          return newFinalValues;
        });
        toast.success(
          `Switched all requisitions to ${
            newType === "FIXED" ? "Fixed" : newType === "DAM_PLUS" ? "DAM+" : "DAM-"
          } for ${counterparty.companyName}. Please enter energy rates again.`,
        );
      } else {
        setFinalValues((prev) => {
          const newFinalValues = { ...prev };
          const selfReqIndex = String(dataWithIndex[0].matches[matchIndex].selfSlot.id);
          if (!newFinalValues[selfReqIndex]) newFinalValues[selfReqIndex] = {};
          if (!newFinalValues[selfReqIndex][counterparty.bidId])
            newFinalValues[selfReqIndex][counterparty.bidId] = {};
          const matchSlotsCount = counterparty.matchSlots?.length || 0;
          for (let i = 1; i <= matchSlotsCount; i++) {
            const currentRate =
              newFinalValues[selfReqIndex][counterparty.bidId][`req${i}_energyRate`];
            newFinalValues[selfReqIndex][counterparty.bidId][`req${i}_energyRateStr`] = newType;
            if (currentRate !== undefined)
              newFinalValues[selfReqIndex][counterparty.bidId][`req${i}_energyRate`] = currentRate;
          }
          return newFinalValues;
        });
      }
    },
    [selectedSelfReq, dataWithIndex, getCounterparties, finalValues, getDefaultValues],
  );

  const handleUserSelectBid = useCallback(
    (data, item, matchIndex) => {
      const selfSlot = wholeData[0]?.matches?.[matchIndex]?.selfSlot;
      const uniqueId = `${selfSlot?.id}-${data.id}`;
      const enrichedBid = {
        ...data,
        name: item.generatorName || item.consumerName,
        company: item.companyName,
        consumerGeneratorBidId: item.bidId,
        counterpartyEntityId: item?.consumerId || item?.generatorId,
        traderId: item?.traderId,
        selected: true,
        selfRequisitionIndex: `${matchIndex}-${item.matchSlots?.findIndex((slot) => slot.id === data.id) || 0}`,
        uniqueId,
        stableId: `${matchIndex}-${item.matchSlots?.findIndex((slot) => slot.id === data.id) || 0}-${item.bidId}-${item.companyName}`,
        selfSlotId: selfSlot?.id,
        matchIndex,
      };
      const reqKey = `${item.bidId}-${selectedSelfReq.id}-${data.id}`;

      setSelectedBids((prevSelectedBids) => {
        const existingIndex = prevSelectedBids.findIndex(
          (bid) => bid.uniqueId === uniqueId && bid.matchIndex === matchIndex,
        );
        if (existingIndex !== -1) {
          setCollapsedRequisitions((prev) => new Set([...prev, reqKey]));
          return prevSelectedBids.filter(
            (bid) => !(bid.uniqueId === uniqueId && bid.matchIndex === matchIndex),
          );
        } else {
          setCollapsedRequisitions((prev) => { const s = new Set(prev); s.delete(reqKey); return s; });
          return [...prevSelectedBids, enrichedBid];
        }
      });
    },
    [wholeData, selectedSelfReq],
  );

  const handleCompanyToggle = useCallback(
    (item, matchIndex) => {
      const allSlots = item.matchSlots || [];
      const selfSlot = wholeData[0]?.matches?.[matchIndex]?.selfSlot;
      const allSelected = allSlots.every((slot) =>
        selectedBids.some(
          (bid) =>
            bid.id === slot.id && bid.selfSlotId === selfSlot?.id && bid.matchIndex === matchIndex,
        ),
      );
      const companyKey = `${item.bidId}-${matchIndex}`;

      setSelectedBids((prevSelectedBids) => {
        if (allSelected) {
          setCollapsedCompanies((prev) => new Set([...prev, companyKey]));
          return prevSelectedBids.filter(
            (bid) =>
              !(
                allSlots.some((slot) => slot.id === bid.id) &&
                bid.selfSlotId === selfSlot?.id &&
                bid.matchIndex === matchIndex
              ),
          );
        } else {
          setCollapsedCompanies((prev) => { const s = new Set(prev); s.delete(companyKey); return s; });
          const slotsToAdd = allSlots.filter(
            (slot) =>
              !prevSelectedBids.some(
                (bid) =>
                  bid.id === slot.id &&
                  bid.selfSlotId === selfSlot?.id &&
                  bid.matchIndex === matchIndex,
              ),
          );
          const mappedSlots = slotsToAdd.map((slot, slotIndex) => ({
            ...slot,
            name: item.generatorName || item.consumerName,
            company: item.companyName,
            counterpartyEntityId: item?.consumerId || item?.generatorId,
            traderId: item?.traderId,
            consumerGeneratorBidId: item.bidId,
            selected: true,
            selfRequisitionIndex: `${matchIndex}-${item.matchSlots?.findIndex((s) => s.id === slot.id) || slotIndex}`,
            uniqueId: `${selfSlot?.id}-${slot.id}`,
            stableId: `${matchIndex}-${item.matchSlots?.findIndex((s) => s.id === slot.id) || slotIndex}-${item.bidId}-${item.companyName}`,
            selfSlotId: selfSlot?.id,
            matchIndex,
          }));
          return [...prevSelectedBids, ...mappedSlots];
        }
      });
    },
    [selectedBids, wholeData],
  );

  // ─── UI computed ─────────────────────────────────────────────────────────
  const isCompanyCollapsed = useCallback(
    (bidId, matchIndex) => collapsedCompanies.has(`${bidId}-${matchIndex}`),
    [collapsedCompanies],
  );

  const isRequisitionCollapsed = useCallback(
    (bidId, matchIndex, slotId) => collapsedRequisitions.has(`${bidId}-${matchIndex}-${slotId}`),
    [collapsedRequisitions],
  );

  const isAllSlotsSelected = useCallback(
    (bidId, matchIndex) => {
      if (!dataWithIndex[0]?.matches?.[matchIndex]) return false;
      const counterparties = getCounterparties(dataWithIndex[0].matches[matchIndex]);
      const counterparty = counterparties.find((cp) => cp.bidId === bidId);
      if (!counterparty?.matchSlots) return false;
      const selfSlot = dataWithIndex[0].matches[matchIndex].selfSlot;
      return counterparty.matchSlots.every((slot) =>
        selectedBids.some(
          (bid) => bid.id === slot.id && bid.selfSlotId === selfSlot.id && bid.matchIndex === matchIndex,
        ),
      );
    },
    [dataWithIndex, selectedBids, getCounterparties],
  );

  const calculateBucketFillPercentage = useCallback(
    (matchIndex) => {
      if (!dataWithIndex[0]?.matches?.[matchIndex]) return 0;
      const match = dataWithIndex[0].matches[matchIndex];
      const selfSlot = match.selfSlot;
      const totalCapacity = selfSlot.purchaseEnergy || selfSlot.sellEnergy || 0;
      if (totalCapacity === 0) return 0;

      const counterparties = getCounterparties(match);
      let totalSelectedQuantity = 0;

      counterparties.forEach((counterparty) => {
        const matchSlots = counterparty.matchSlots || [];
        const selfReqIndex = String(selfSlot.id);
        const defaultValues = getDefaultValues(matchSlots.length, selfSlot, matchSlots, counterparty);

        counterparty.matchSlots?.forEach((slot, slotIndex) => {
          const isSelected = selectedBids.some(
            (bid) =>
              bid.id === slot.id && bid.selfSlotId === selfSlot.id && bid.matchIndex === matchIndex,
          );
          if (isSelected) {
            const editedQuantity =
              finalValues?.[selfReqIndex]?.[counterparty.bidId]?.[`req${slotIndex + 1}_quantity`];
            const quantity = parseFloat(
              editedQuantity ?? defaultValues[`req${slotIndex + 1}_quantity`],
            );
            if (!isNaN(quantity) && quantity > 0) totalSelectedQuantity += quantity;
          }
        });
      });

      const percentage = (totalSelectedQuantity / totalCapacity) * 100;
      return isNaN(percentage) ? 0 : Math.round(percentage);
    },
    [dataWithIndex, selectedBids, getCounterparties, finalValues, getDefaultValues],
  );

  const goToPrevReq = useCallback(() => {
    const currentIdx = uniqueSelfReq.findIndex(
      (req) => req.selfRequisitionIndex === selectedSelfReq?.selfRequisitionIndex,
    );
    if (currentIdx > 0) setSelectedSelfReq(uniqueSelfReq[currentIdx - 1]);
  }, [uniqueSelfReq, selectedSelfReq]);

  const goToNextReq = useCallback(() => {
    const currentIdx = uniqueSelfReq.findIndex(
      (req) => req.selfRequisitionIndex === selectedSelfReq?.selfRequisitionIndex,
    );
    if (currentIdx < uniqueSelfReq.length - 1) setSelectedSelfReq(uniqueSelfReq[currentIdx + 1]);
  }, [uniqueSelfReq, selectedSelfReq]);

  const hasFieldErrors = useCallback(
    () => Object.keys(fieldErrors).length > 0,
    [fieldErrors],
  );

  // ─── Return ───────────────────────────────────────────────────────────────
  return {
    // State
    uniqueSelfReq,
    selectedSelfReq,
    setSelectedSelfReq,
    finalValues,
    loading,
    conformOpen,
    setConformOpen,
    dataWithIndex,
    wholeData,
    rejectAllTermsConfirm,
    setRejectAllTermsConfirm,
    rejectLoading,
    selectedBids,
    fieldErrors,
    setFieldErrors,
    chargeModal,
    setChargeModal,
    chargeDetails,
    setChargeDetails,
    rejectContract,
    setRejectContract,
    // Redux
    selfEntityId,
    activeRole,
    listingType,
    mainScreenMatchData,
    user,
    // Derived
    isConsumer,
    isGenerator,
    // Handlers
    handleInputChange,
    handleEnergyRateTypeChange,
    handleUserSelectBid,
    handleCompanyToggle,
    handleTermsSubmit,
    handleDirectTerms,
    handleChargeFinal,
    handleDirectTermsSumbit,
    handleRejectAllTermsSubmit,
    handleInitPayment,
    // Computed helpers
    getCounterparties,
    getCurrentValues,
    getDefaultValues,
    getMaxQuantity,
    getAllFilledFinalValues,
    validateStartDateTimes,
    isCompanyCollapsed,
    isRequisitionCollapsed,
    isAllSlotsSelected,
    calculateBucketFillPercentage,
    hasFieldErrors,
    goToPrevReq,
    goToNextReq,
  };
};

export default useTermsCondition;
